import type {
  ExperienceStatus,
  MerchantExperienceParticipantDto,
  MerchantExperienceReviewDto,
  MerchantExperienceReviewReplyDto,
  MerchantExperienceDetailDto,
  MerchantExperienceDto,
  MerchantExperienceImageDto,
  MerchantExperienceRecurrenceRequestDto,
  MerchantExperienceRecurrenceResultDto,
  MerchantExperienceRequestDto,
  MerchantExperienceSessionDto,
  MerchantExperienceSessionRequestDto,
  Page,
  PageQuery,
  PassPlanOptionDto,
} from '@kardume/types';
import api from './http';

/**
 * Experiências no painel do lojista (cards #347 e #348).
 *
 * **A loja sai do token, nunca da URL.** Nenhuma rota aqui tem `{merchantId}`:
 * com o id no caminho, qualquer lojista autenticado abriria a agenda e a lista
 * de participantes do vizinho - e participante é dado pessoal de cliente.
 *
 * Cliente próprio, e não mais um bloco no `adminApi`, pelo mesmo motivo do
 * `backofficeExperienceApi`: o domínio é grande (catálogo, fotos, agenda,
 * vagas, participantes, avaliações) e cabe inteiro num arquivo só.
 */
export const merchantExperienceApi = {
  /* ------------------------------------------------------------ catálogo */

  list(
    query?: PageQuery & { status?: ExperienceStatus }
  ): Promise<Page<MerchantExperienceDto>> {
    return api
      .get<Page<MerchantExperienceDto>>('/v1/merchant/experiences', {
        params: {
          page: query?.page ?? 0,
          size: query?.size ?? 20,
          ...(query?.sort ? { sort: query.sort } : {}),
          ...(query?.status ? { status: query.status } : {}),
        },
      })
      .then((r) => r.data);
  },

  /** Detalhe com fotos e a agenda inteira - passado inclusive. */
  detail(experienceId: string): Promise<MerchantExperienceDetailDto> {
    return api
      .get<MerchantExperienceDetailDto>(`/v1/merchant/experiences/${experienceId}`)
      .then((r) => r.data);
  },

  /** Nasce `PENDING_REVIEW`: o que a loja publica passa pela moderação. */
  create(payload: MerchantExperienceRequestDto): Promise<MerchantExperienceDto> {
    return api.post<MerchantExperienceDto>('/v1/merchant/experiences', payload).then((r) => r.data);
  },

  /** Editar uma experiência recusada a devolve automaticamente para a fila. */
  update(
    experienceId: string,
    payload: MerchantExperienceRequestDto
  ): Promise<MerchantExperienceDto> {
    return api
      .put<MerchantExperienceDto>(`/v1/merchant/experiences/${experienceId}`, payload)
      .then((r) => r.data);
  },

  /** Pausar, retomar ou encerrar. Aprovar e recusar são do backoffice. */
  changeStatus(experienceId: string, status: ExperienceStatus): Promise<MerchantExperienceDto> {
    return api
      .patch<MerchantExperienceDto>(`/v1/merchant/experiences/${experienceId}/status`, null, {
        params: { status },
      })
      .then((r) => r.data);
  },

  /** Soft delete. Recusa enquanto houver data futura com reservas. */
  remove(experienceId: string): Promise<void> {
    return api.delete(`/v1/merchant/experiences/${experienceId}`).then(() => undefined);
  },

  /* --------------------------------------------------------------- fotos */

  /** A capa é a foto de menor `sortOrder` - não existe campo de capa. */
  addImage(
    experienceId: string,
    payload: { url: string; sortOrder: number }
  ): Promise<MerchantExperienceImageDto> {
    return api
      .post<MerchantExperienceImageDto>(`/v1/merchant/experiences/${experienceId}/images`, payload)
      .then((r) => r.data);
  },

  removeImage(experienceId: string, imageId: string): Promise<void> {
    return api
      .delete(`/v1/merchant/experiences/${experienceId}/images/${imageId}`)
      .then(() => undefined);
  },

  /* -------------------------------------------------------------- agenda */

  sessions(experienceId: string): Promise<MerchantExperienceSessionDto[]> {
    return api
      .get<MerchantExperienceSessionDto[]>(`/v1/merchant/experiences/${experienceId}/sessions`)
      .then((r) => r.data);
  },

  addSession(
    experienceId: string,
    payload: MerchantExperienceSessionRequestDto
  ): Promise<MerchantExperienceSessionDto> {
    return api
      .post<MerchantExperienceSessionDto>(
        `/v1/merchant/experiences/${experienceId}/sessions`,
        payload
      )
      .then((r) => r.data);
  },

  /**
   * Abre a agenda recorrente. O retorno traz `created`, que pode ser menor que
   * o número de datas pedidas: as que colidem com o que já existe são puladas.
   */
  addRecurrence(
    experienceId: string,
    payload: MerchantExperienceRecurrenceRequestDto
  ): Promise<MerchantExperienceRecurrenceResultDto> {
    return api
      .post<MerchantExperienceRecurrenceResultDto>(
        `/v1/merchant/experiences/${experienceId}/sessions/recurrence`,
        payload
      )
      .then((r) => r.data);
  },

  /**
   * Muda o limite de vagas. Aumentar promove a fila de espera na hora; o DTO
   * devolvido já vem relido, mas a tela recarrega a agenda para os contadores
   * das outras datas acompanharem.
   */
  changeSessionCapacity(sessionId: string, capacity: number): Promise<MerchantExperienceSessionDto> {
    return api
      .patch<MerchantExperienceSessionDto>(
        `/v1/merchant/experiences/sessions/${sessionId}/capacity`,
        null,
        { params: { capacity } }
      )
      .then((r) => r.data);
  },

  /** Cancela a data e todas as reservas vivas dela, avisando quem reservou. */
  cancelSession(sessionId: string): Promise<MerchantExperienceSessionDto> {
    return api
      .delete<MerchantExperienceSessionDto>(`/v1/merchant/experiences/sessions/${sessionId}`)
      .then((r) => r.data);
  },

  /** Cancela a série - só as datas futuras. Devolve quantas foram canceladas. */
  cancelRecurrence(recurrenceGroupId: string): Promise<number> {
    return api
      .delete<{ canceled: number }>(
        `/v1/merchant/experiences/sessions/recurrence/${recurrenceGroupId}`
      )
      .then((r) => r.data.canceled);
  },

  /* ------------------------------------------------------- participantes */

  /**
   * Quem vai à data: confirmados, fila de espera, presenças, cancelamentos e
   * faltas.
   *
   * **Sem o token do QR Code** - o core não o devolve e a tela não deve
   * pedi-lo. Participante é dado pessoal de cliente: esta lista nunca sai da
   * sessão autenticada.
   */
  participants(sessionId: string): Promise<MerchantExperienceParticipantDto[]> {
    return api
      .get<MerchantExperienceParticipantDto[]>(
        `/v1/merchant/experiences/sessions/${sessionId}/participants`
      )
      .then((r) => r.data);
  },

  /**
   * Marca não comparecimento. **Não devolve a vaga** - o lugar ficou separado e
   * a loja não pôde vendê-lo. Só depois de a data começar, e só sobre reserva
   * confirmada.
   */
  markNoShow(reservationId: string): Promise<MerchantExperienceParticipantDto> {
    return api
      .post<MerchantExperienceParticipantDto>(
        `/v1/merchant/experiences/reservations/${reservationId}/no-show`
      )
      .then((r) => r.data);
  },

  /* ----------------------------------------------------------- avaliações */

  /** Notas das experiências da loja - separadas das avaliações da loja. */
  reviews(query?: PageQuery): Promise<Page<MerchantExperienceReviewDto>> {
    return api
      .get<Page<MerchantExperienceReviewDto>>('/v1/merchant/experiences/reviews', {
        params: { page: query?.page ?? 0, size: query?.size ?? 20 },
      })
      .then((r) => r.data);
  },

  replyReview(
    reviewId: string,
    payload: MerchantExperienceReviewReplyDto
  ): Promise<MerchantExperienceReviewDto> {
    return api
      .post<MerchantExperienceReviewDto>(
        `/v1/merchant/experiences/reviews/${reviewId}/reply`,
        payload
      )
      .then((r) => r.data);
  },

  /* ------------------------------------------------------------ auxiliar */

  /**
   * Planos do Kardume Pass para o seletor de "exclusiva Pass a partir de X".
   *
   * Rota pública do consumidor: é a única que lista os planos sem exigir
   * permissão de backoffice, e o painel só precisa de id e nome.
   */
  listPassPlanOptions(): Promise<PassPlanOptionDto[]> {
    return api.get<PassPlanOptionDto[]>('/v1/pass/plans').then((r) => r.data);
  },
};
